// This offset is part of every customer-facing tenant URL. Once those URLs
// appear in emails or bookmarks it can never change without breaking links.
export const RID_URL_OFFSET = 99;

const GLOBAL_PATH_PREFIXES = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/command-center',
];

export function isRestaurantId(value) {
  const stringValue = String(value ?? '');
  if (!/^[1-9]\d*$/.test(stringValue)) return false;
  const numberValue = Number(stringValue);
  return Number.isSafeInteger(numberValue);
}

function positiveIntegerString(value) {
  return isRestaurantId(value) ? String(value) : null;
}

export function toUrlRid(restaurantId) {
  const normalized = positiveIntegerString(restaurantId);
  if (normalized == null) return null;
  const urlRid = Number(normalized) + RID_URL_OFFSET;
  return Number.isSafeInteger(urlRid) ? String(urlRid) : null;
}

export function fromUrlRid(urlRid) {
  const normalized = positiveIntegerString(urlRid);
  if (normalized == null) return null;
  const restaurantId = Number(normalized) - RID_URL_OFFSET;
  return restaurantId > 0 ? String(restaurantId) : null;
}

export function parseTenantPath(pathname) {
  const match = String(pathname ?? '').match(/^\/r\/([^/]+)(\/.*)?$/);
  if (!match) return { rid: null, path: pathname, invalid: false };
  const rid = fromUrlRid(match[1]);
  return {
    rid,
    path: match[2] || '/',
    invalid: rid == null,
    urlRid: match[1],
  };
}

export function isGlobalPath(pathname) {
  const pathOnly = pathname.split(/[?#]/, 1)[0];
  return GLOBAL_PATH_PREFIXES.some(prefix => (
    pathOnly === prefix || pathOnly.startsWith(`${prefix}/`)
  ));
}

function prefixPath(urlRid, pathname) {
  if (typeof pathname !== 'string' || !pathname.startsWith('/')) return pathname;
  if (pathname.startsWith('/r/') || isGlobalPath(pathname)) return pathname;
  return urlRid == null ? pathname : `/r/${urlRid}${pathname}`;
}

function prefixDestination(urlRid, to) {
  if (typeof to === 'number') return to;
  if (typeof to === 'string') return prefixPath(urlRid, to);
  if (to == null || typeof to !== 'object') return to;
  return { ...to, pathname: prefixPath(urlRid, to.pathname) };
}

export function withTenant(restaurantId, to) {
  return prefixDestination(toUrlRid(restaurantId), to);
}

// Invalid tenant URLs stay invalid while the user interacts with their shell.
// Otherwise a click would escape to a bare legacy path and could resolve to a
// different restaurant before TenantGate has shown its not-found result.
export function withTenantUrlRid(urlRid, to) {
  return prefixDestination(urlRid, to);
}

export function tenantUrl(restaurantId, path) {
  const urlRid = toUrlRid(restaurantId);
  if (urlRid == null) {
    throw new TypeError('tenantUrl requires a valid restaurant id');
  }
  if (typeof path !== 'string' || !path.startsWith('/') || path.startsWith('/r/') || isGlobalPath(path)) {
    throw new TypeError('tenantUrl requires an unprefixed customer-app path');
  }
  return prefixDestination(urlRid, path);
}
